<template>
  <div
    class="item-container"
    :class="{
      'display-item-container': displayItemView,
      'resize-cursor': initialMousePositionOnResize,
    }"
    @mousemove="resizeDrag"
    @mouseup="resizeStop">

    <div class="item-view-top-bar">
      {{ fullTitle || currentCard.title }}
    </div>

    <div
      class="item-view-heading">
      <div
        class="item-view-heading-items">
        <div class="item-view-heading-tab-divider" />
        <div
          class="item-view-heading-item-tab tab-collection"
          @click="$goToCollection({ id: selectedCollection })">
          <div class="item-tab-text">
            <i class="mdi mdi-arrow-left" />
            Collection
          </div>
        </div>
        <div class="item-view-heading-tab-divider" />
        <div
          class="item-view-heading-item-tab tab-icon">
          <div class="item-tab-text">
            <i class="mdi mdi-paperclip mdi-rotate-45" />
          </div>
        </div>
        <div
          v-for="(file, index) in webViewerFileData"
          :key="file.cardId || -index"
          class="item-view-heading-item-tab"
          :class="{ 'item-view-heading-item-tab-active': file.cardId === cardId }"
          @mouseenter="fullTitle = file.ref.title"
          @mouseleave="fullTitle = null"
          @click="handleItemTabClick(file)">
          <div
            class="item-tab-text">
            {{ file.ref.title }}
          </div>
          <i
            class="mdi mdi-close icon-close-file"
            @click.stop="closeFile(file.cardId, index)" />
        </div>
      </div>
    </div>

    <div class="item-view-content-container">
      <!-- NOTES EDITOR -->
      <ItemViewNotesEditor
        class="notes-container"
        :currentCard="currentCard" />

      <!-- RESIZE DRAG HELPER DIV -->
      <div
        class="resizer"
        :style="{
          'right': webViewerWidth,
        }"
        @mousedown="resizeStart"
        @mousemove="resizeDrag"
        @mouseup="resizeStop">
        <i class="mdi mdi-drag-vertical" />
      </div>

      <!-- WEBVIEWER -->
      <WebViewer
        class="webviewer"
        :style="{
          'flex-basis': webViewerWidth,
        }"
        :dragging="!!initialMousePositionOnResize"
        :path="`${publicPath}lib`" />
    </div>

  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import ItemViewNotesEditor from '@/components/AppView/ItemView/ItemViewNotesEditor.vue';
import WebViewer from '@/components/AppView/WebViewer/WebViewer.vue';
const throttle = require('lodash/throttle');

export default {
  name: 'ItemView',
  components: {
    ItemViewNotesEditor,
    WebViewer,
  },
  data() {
    return {
      publicPath: process.env.BASE_URL,
      draggedWebViewerWidth: 74,
      initialMousePositionOnResize: null,
      fullTitle: null,
    };
  },
  computed: {
    ...mapState({
      displayItemView: state => state.appState.displayItemView,
      webViewerFileData: state => state.files.webViewerFileData,
      navigationHistory: state => state.appState.navigationHistory,
      selectedCollection: state => state.collections.selectedCollection,
    }),
    ...mapGetters({
      getCardRef: 'cards/getCardRef',
    }),
    webViewerWidth() {
      // Ensure a minimum width of 22rem (in vw)
      const minWidth = ((22 * 16) / document.body.clientWidth) * 100;
      return `${Math.max(minWidth, this.draggedWebViewerWidth)}vw`;
    },
    cardId() {
      return Number(this.$route.params.cardId);
    },
    currentCard() {
      // const relatedCollectionId = this.searchIsActive ? null : this.selectedCollection;
      return this.getCardRef({ cardId: this.cardId, relatedCollectionId: this.selectedCollection }) || {};
      // const fileData = this.webViewerFileData.find(entry => entry.cardId === this.cardId);
      // if (fileData) return fileData.ref;
      // return {};
    },
  },
  methods: {
    ...mapMutations({
      DeleteFromHistory: 'appState/DeleteFromHistory',
      RemoveFromWebViewerFileData: 'files/RemoveFromWebViewerFileData',
    }),
    handleItemTabClick(file) {
      if (this.cardId === file.cardId) this.$goToCard({ id: file.cardId, relatedCollectionId: file.ref.relatedCollectionId });
      else this.$goToItem({ id: file.cardId, relatedCollectionId: file.ref.relatedCollectionId });
    },
    async closeFile(cardId, index) {
      this.fullTitle = null;

      this.DeleteFromHistory(`/item/${cardId}`);
      this.RemoveFromWebViewerFileData(cardId);

      const cardIsOpen = cardId === this.cardId;
      if (!cardIsOpen) return;

      if (!this.webViewerFileData.length) {
        const path = this.navigationHistory[1] || `/collection/${this.currentCard.relatedCollectionId}`;
        this.$navigateViaHelper({ path }); // To do: check whether this function is necessary, see comment in helpers.js
      }
      else {
        let newIndex = index;
        if (!this.webViewerFileData[newIndex]) newIndex -= 1; // If we closed the last file in the array, we should go to the previous file

        const { id, relatedCollectionId } = this.webViewerFileData[newIndex].ref;
        this.$goToItem({ id, relatedCollectionId });
      }
    },
    resizeStart($event) {
      this.initialMousePositionOnResize = $event.x;
    },
    resizeDrag: throttle(function ($event) { // eslint-disable-line
      if (!this.initialMousePositionOnResize) return;

      const { x } = $event;
      const newWidth = ((document.body.clientWidth - x) / document.body.clientWidth) * 100;
      this.draggedWebViewerWidth = newWidth;
    }, 10),
    resizeStop() {
      this.initialMousePositionOnResize = null;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~styles/colors';
@import '~styles/layouts';
@import '~styles/fonts';

.resizer {
  height: 100%;
  flex-basis: 0.75rem;
  flex-grow: 0;
  flex-shrink: 0;
  z-index: 10;
  cursor: w-resize;
  padding-top: 30vh;
  background-color: #f8f9fa;
}
.resize-cursor {
  cursor: w-resize;
}
.item-container {
  position: absolute;
  top: 0;
  width: 100vw;
  max-width: 100vw;
  height: 100vh;
  max-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  background: $white;
  border-bottom: 1px solid #dbdbdb;
  box-shadow: -1px 0 1.5px 1px $darkgrey;
  visibility: hidden;
  opacity: 0;
  pointer-events: none;
  transition: all 0.3s ease-out;
  z-index: 10;
  overflow: hidden;
  transform: scale(0);
  transform-origin: 50% 90%;
}
.display-item-container {
  visibility: visible;
  opacity: 1;
  pointer-events: auto;
  transform: scale(1);
}
.item-view-top-bar {
  height: 2rem;
  line-height: 2rem;
  font-family: $headerFont;
  flex-grow: 0;
  flex-shrink: 0;
  background-color: $secondary-dark;
  color: $primary-light;
  text-align: center;
}
.item-view-heading {
  width: 100%;
  height: 2rem;
  height: 2.25rem;
  border-radius: 0.75rem 0 0 0;
  border-bottom: 1px solid #dbdbdb;
  background-color: #f5f5f5;
  text-align: center;
  display: flex;
  user-select: none;
}
.item-view-content-container {
  display: flex;
  justify-content: flex-end;
  align-items: stretch;
}
.item-view-heading-items {
  position: relative;
  display: inline-block;
  flex-grow: 0;
  flex-shrink: 1;
  height: 2.25rem;
  white-space: nowrap;
  text-align: left;
  border-bottom: 1px solid $primary;
  background-color: $lightgrey;
  font-family: $headerFont;
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
}
.item-view-heading-item-tab {
  position: relative;
  display: inline-block;
  width: 10rem;
  max-width: 10rem;
  height: calc(2.25rem - 2px);
  cursor: pointer;
  flex: 0 0 auto;
  text-align: center;
  padding: 0.25rem;
  cursor: pointer;
}
.tab-collection {
  width: 8rem;
  font-weight: bold;
  padding-left: 0;
}
.tab-collection:hover {
  border: none !important;
}
.item-view-heading-tab-divider {
  position: relative;
  display: inline-block;
  flex: 0 0 auto;
  padding-top: 0.3rem;
  height: 1.4rem;
  margin: 0 1.5rem;
  background: $primary;
  border-left: 1px solid $primary;
}
.item-view-heading-tab-divider:first-child {
  margin: 0 1rem 0 1rem;
}
.item-view-heading-tab-divider:last-child {
  margin: 0 0rem 0 1rem;
}
.item-view-heading-tab-divider:nth-child(4) {
  margin: 0 1rem 0 1rem;
}
.item-view-heading-item-tab:hover {
  border-bottom: 2px solid $primary;
}
.item-view-heading-item-tab-active {
  border-bottom: 2px solid $secondary-dark !important;
  font-weight: 600;
}
.tab-icon {
  width: 2rem;
}
.tab-icon:hover {
  border: none;
  cursor: default;
}
.tab-icon i {
  position: relative;
  top: 0.15rem;
  right: 0.5rem;
}
.item-tab-text {
  max-width: calc(10rem - 1.5rem);
  font-size: 0.9rem;
  font-size: 1rem;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-left: 0.5rem;
}
.icon-close-file {
  position: absolute;
  top: 0.25rem;
  right: 0.5rem;
}
.webviewer {
  position: relative;
  flex-grow: 0;
  flex-shrink: 0;
  max-width: calc(99.75vw - 15vw); // 15vw is min width notes container
}
.top-bar {
  position: absolute;
  top: 0;
  right: 0;
  height: 47px;
  width: 100%;
  border-bottom: 1px solid #dbdbdb;
}
.notes-container {
  position: relative;
  flex-grow: 1;
  flex-shrink: 1;
  min-width: 15vw;
}
</style>
